import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { camelCase, snakeCase } from 'lodash'
import { useDispatchAsync } from '@src/utils/dispatch'
import {
  CoachesCoachingKind,
  EhrAppointmentSchedulingInfo,
  EhrAppointmentSchedulingInfoCollection,
  EhrAppointmentSchedulingMode,
} from '@src/types'
import { EhrAppointmentSchedulingInfoQuery } from '../../graphql/ehrAppointmentSchedulingInfo.generated'
import { ehrAppointmentSchedulingInfoSelector } from '../../models/nutritionistHub.selectors'

type SchedulingInfoCollection = NonNullable<
  EhrAppointmentSchedulingInfoQuery['ehrAppointmentSchedulingInfo']
>
type SchedulingInfoKind = keyof Omit<SchedulingInfoCollection, '__typename'>

interface CallSchedulingState {
  promptForInsurance: boolean
  canSchedule: boolean
  // Partial: the store value can be hydrated with only a subset of the
  // coaching kinds (e.g. mnt missing), so entries must be guarded.
  schedulingInfo?: Partial<SchedulingInfoCollection>
}

interface CallSchedulingOptions {
  skip?: boolean
  only?: CoachesCoachingKind
}

export const getSchedulingInfoForCoachingKind = (
  allSchedulingInfo:
    | EhrAppointmentSchedulingInfoCollection
    | Partial<SchedulingInfoCollection>
    | null
    | undefined,
  coachingKind: CoachesCoachingKind,
): EhrAppointmentSchedulingInfo | undefined => {
  const key = camelCase(coachingKind) as keyof SchedulingInfoCollection

  if (!allSchedulingInfo?.[key]) {
    return
  }
  if (!Object.prototype.hasOwnProperty.call(allSchedulingInfo[key], 'schedulingMode')) {
    return
  }
  return allSchedulingInfo[key] as EhrAppointmentSchedulingInfo
}

// Load scheduling info and determine the user's overall scheduling state.
//
// @param {boolean} [options.skip] Skip loading appointment scheduling info,
//  e.g. if the consumer has already loaded it.
// @param {CoachesCoachingKind} [options.only] Only consider one specific kind of scheduling info
//  when evaluating the user's scheduling state.
//
// @returns {Object} with the following properties:
// - promptForInsurance {boolean} true if any scheduling info has an undetermined schedulingMode.
// - canSchedule {boolean} true if any scheduling info has a schedulingMode other than 'none'
// - schedulingInfo
// - loading
// - error
//
export const useCallSchedulingState = ({ skip, only }: CallSchedulingOptions = {}) => {
  const dispatchAsync = useDispatchAsync()
  const [loading, setLoading] = useState(() => !skip)
  const [error, setError] = useState<string>()

  const allSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)

  useEffect(() => {
    if (skip) {
      return
    }
    const asyncEffect = async () => {
      try {
        await dispatchAsync({
          type: 'nutritionistHub/fetchAppointmentSchedulingInfo',
        })
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    asyncEffect()
  }, [skip, dispatchAsync])

  const schedulingState: CallSchedulingState = useMemo(() => {
    if (!allSchedulingInfo) {
      return {
        promptForInsurance: false,
        canSchedule: false,
        schedulingInfo: undefined,
      }
    }
    const schedulingInfo = allSchedulingInfo as Partial<SchedulingInfoCollection>

    // Each of 'mnt', 'stressManagement', or any other coaching kind we add to the scheduling info operation
    const schedulingKinds = only
      ? [camelCase(only) as SchedulingInfoKind]
      : (Object.keys(schedulingInfo).filter((key) =>
          getSchedulingInfoForCoachingKind(
            allSchedulingInfo,
            snakeCase(key) as CoachesCoachingKind,
          ),
        ) as SchedulingInfoKind[])

    const defaultState: CallSchedulingState = {
      promptForInsurance: false,
      canSchedule: false,
      schedulingInfo,
    }

    return schedulingKinds.reduce((state, kind) => {
      const info = schedulingInfo[kind]
      if (!info) {
        return state
      }
      return {
        ...state,
        promptForInsurance:
          state.promptForInsurance ||
          info.schedulingMode === EhrAppointmentSchedulingMode.Undetermined,
        canSchedule: state.canSchedule || info.schedulingMode !== EhrAppointmentSchedulingMode.None,
      }
    }, defaultState)
  }, [only, allSchedulingInfo])

  return { ...schedulingState, loading, error }
}
